import React, { useState, useEffect } from "react";
import { API_BASE_URL, ADMIN_URL } from './config.js';
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { 
  LockOutlined, 
  PersonOutlined,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Load saved username if "Remember Me" was checked
  useEffect(() => {
    const savedUsername = localStorage.getItem("rememberedUsername");
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/login/`, {
        username,
        password,
      });
      
      // Always store access token and username
      localStorage.setItem("access", response.data.access);
      localStorage.setItem("username", response.data.username);
      localStorage.setItem("ai_access", response.data.ai_access === false ? "false" : "true");
      
      // Handle "Remember Me" functionality
      if (rememberMe) {
        localStorage.setItem("rememberedUsername", username);
      } else {
        localStorage.removeItem("rememberedUsername");
      }
      
      navigate("/home");
    } catch (err) {
      setError(err.response?.data?.error || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && username && password) {
      handleLogin(e);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default'
      }}
    >
      {/* Top Banner */}
      <Box
        sx={{
          bgcolor: '#f9a91e',
          color: 'text.primary',
          py: 1.5,
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: { xs: '0.875rem', sm: '1rem' },
          letterSpacing: 1,
          boxShadow: 1
        }}
      >
        SALES REP PORTAL
      </Box>

      {/* Main Content */}
      <Container 
        component="main" 
        maxWidth="xs"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: { xs: 4, sm: 8 }
        }}
      >
        {/* Login Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(31, 38, 135, 0.07)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Box
              component="img"
              src="//gibsonhomewares.com/cdn/shop/files/GibsonHomewares_Logo_140x@2x.png"
              alt="Gibson Homewares"
              sx={{
                height: 50,
                width: 'auto',
                maxWidth: 180,
                mb: 2,
              }}
            />
            <Typography
              component="h5"
              variant="h3"
              sx={{
                color: '#1d5576',
                fontWeight: 500,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              Please sign in to your account
            </Typography>
          </Box>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  alignItems: 'center'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username/Email"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlined color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: '#1d5576',
                  },
                }
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&.Mui-focused fieldset': {
                    borderColor: '#1d5576',
                  },
                }
              }}
            />

            {/* Remember Me and Forgot Password Row */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mt: 1,
                width: '100%'
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: '#1d5576',
                      '&.Mui-checked': {
                        color: '#1d5576',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Remember me
                  </Typography>
                }
              />
              
              <Link 
                to="/forgot-password" 
                style={{ 
                  color: '#1d5576', 
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || !username || !password}
              sx={{
                mt: 1,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                bgcolor: '#1d5576',
                '&:hover': {
                  bgcolor: '#17465f',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(29, 85, 118, 0.3)',
                },
                '&:disabled': {
                  bgcolor: '#e0e0e0',
                },
                transition: 'all 0.3s ease',
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <Typography
            variant="caption"
            color="#1d5576"
            align="center"
            component="a"
            href={ADMIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'block',
              fontSize: '0.75rem',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
                color: '#1d5576',
              }
            }}
          >
            Go to Admin Panel
          </Typography>
        </Paper>

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.75rem'
            }}
          >
            © {new Date().getFullYear()} Gibson Homewares. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Login;